/**
 * Where the holder is, kept in the browser's history so the phone's own back
 * gesture walks the application instead of closing it.
 *
 * Before this module nothing wrote to the history. An installed application
 * opened with one entry, every screen replaced the last one in place, and the
 * Android back button left the application from any screen, with a sheet
 * open or a camera running. Every screen and every sheet is now an entry, and
 * the bar's own back and forward buttons move through the same entries, so
 * the two ways back never disagree about where back is.
 *
 * Entries are pushed only from a tap. Chrome skips, on back, any entry a page
 * added without a user gesture since the last one, so an entry pushed from a
 * timer or at boot would be one the back button jumps over.
 *
 * A place is a screen and, on the card, at most one layer over it: the code,
 * or one of the three sheets. `depth` counts entries from the one the
 * application opened on, so the card knows when there is nothing behind it
 * and `top` knows whether there is anything ahead.
 */

export type View = "card" | "scan" | "history" | "errand-create" | "errand-receive" | "challenge";
export type Layer = "code" | "menu" | "errands" | "logout";

export interface Place {
  view: View;
  layer: Layer | null;
}

interface Entry extends Place {
  uha: 1;
  depth: number;
}

/** Sheets close when the holder leaves the card for another screen: coming
 *  back should find the card, not the menu they tapped through. The code is
 *  not a sheet; «Responder a un desafío» comes back to it. */
const TRANSIENT: ReadonlySet<Layer> = new Set(["menu", "errands", "logout"]);

const listeners = new Set<(place: Place) => void>();
let top = 0;

function entry(): Entry {
  const state = history.state as Entry | null;
  return state?.uha === 1 ? state : { uha: 1, depth: 0, view: "card", layer: null };
}

function tell(): void {
  const { view, layer } = entry();
  for (const listener of [...listeners]) listener({ view, layer });
}

// Guarded because the unit tests import the screens, and with them this
// module, under Node, where there is no window and nothing to navigate.
if (typeof window !== "undefined") window.addEventListener("popstate", () => tell());

/** Called on every move, back or forward, by tap or by the phone's button. */
export function onPlace(listener: (place: Place) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function here(): Place {
  const { view, layer } = entry();
  return { view, layer };
}

export const canGoBack = () => entry().depth > 0;
export const canGoForward = () => entry().depth < top;

/**
 * The entry the application starts on, which is always the card. A launch
 * from the home screen is a new document with no entries of ours. A reload
 * keeps the history, and the only reload this application does on its own is
 * a new release taking over (`main.ts`), which should land on the card like a
 * launch; the history is walked back to its first entry, so back from there
 * leaves and forward returns to where the holder was.
 */
export function startHere(): Place {
  const current = entry();
  top = current.depth;
  history.replaceState({ ...current, layer: null }, "");
  if (current.depth > 0) history.go(-current.depth);
  return { view: "card", layer: null };
}

function push(place: Place): void {
  const depth = entry().depth + 1;
  history.pushState({ uha: 1, depth, ...place } satisfies Entry, "");
  top = depth;
  tell();
}

/**
 * To another screen. From a sheet, the screen takes the sheet's entry rather
 * than one of its own: back then lands on the card the sheet was opened over,
 * in one press. Pushed on top instead, the entry behind it would be the card
 * again with the sheet closed, and that first back would change nothing on
 * screen.
 */
export function go(view: View): void {
  const current = entry();
  if (current.layer && TRANSIENT.has(current.layer)) {
    history.replaceState({ ...current, view, layer: null } satisfies Entry, "");
    top = current.depth;
    tell();
    return;
  }
  push({ view, layer: null });
}

/** A layer over the card, as its own entry: back closes it. */
export function open(layer: Layer): void {
  push({ view: entry().view, layer });
}

/** One layer for another in the same entry, such as the menu giving way to
 *  the logout question: back from that question goes to the card. */
export function swap(layer: Layer | null): void {
  history.replaceState({ ...entry(), layer }, "");
  tell();
}

export function back(): void {
  if (canGoBack()) history.back();
}

export function forward(): void {
  if (canGoForward()) history.forward();
}

/** Straight to the card the application started on, whatever lies between:
 *  «Listo» and «Volver a mi identidad» at the end of a flow. */
export function home(): void {
  const { depth } = entry();
  if (depth > 0) history.go(-depth);
}
